import { FC, FormEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Dialog, Loader, SearchableSelect, SearchableSelectOption, useDialog } from 'components';
import { TextField } from 'components/inputs/text-field';
import DataTable, { DataTableRef } from 'components/tables/data-table';
import { useAuth, useSizeTypes } from 'contexts';
import dayjs from 'dayjs';
import { useBookingApi } from 'hooks/use-booking-api';
import useReservation, { ReservationUnit } from 'hooks/use-reservation';
import unitDataColumns, { defaultUnitColumns } from 'pages/units/grid/unit-columns';
import { downloadUngroupedCsv, fetchActiveUnitsUngrouped, getUnitByUnitNumber } from 'services';
import { ActiveUnit, Booking, BookingDetails, Role } from 'types';

const BookingUnits: FC<Partial<Booking>> = booking => {
  const { userProfile } = useAuth();
  const { containerSizeTypeEntries, chassisSizeTypeEntries, gensetSizeTypeEntries } = useSizeTypes();
  const { reserveUnit, unreserveUnit } = useBookingApi();
  const { ref, openDialog, closeDialog } = useDialog({});
  const { closeDialog: closeSubmitDialog } = useDialog();

  const dataTableRef = useRef<DataTableRef>(null);

  const [unit, setUnit] = useState<ReservationUnit>({});
  const [deleteUnit, setDeleteUnit] = useState<ActiveUnit>();
  const [loading, setLoading] = useState<boolean>(false);

  const [unitIsActive, setUnitIsActive] = useState<boolean | null>(null);
  useEffect(() => {
    if(!!unit.unitNumber) {
      getUnitByUnitNumber(unit?.unitNumber).then((result) => {
        setUnitIsActive(!!result?.active);
      }).catch(() => {
        setUnitIsActive(false);
      })
    }
  }, [unit.unitNumber, setUnitIsActive]);

  useEffect(() => console.log(unitIsActive), [unitIsActive]);

  const bookingDetails: BookingDetails[] = useMemo(
    () => Object.assign([], booking.bookingDetails),
    [booking.bookingDetails]
  );

  const initialColumns = useMemo(() => {
    return userProfile.columnPreferences.filter(cp => cp.table === 'UNIT_UNSTACKED').length > 0
      ? userProfile.columnPreferences
          .filter(cp => cp.table === 'UNIT_UNSTACKED')
          .map(({ column, width }) => {
            return { field: column, width };
          })
      : defaultUnitColumns.map(c => {
          return { field: c, width: 80 };
        });
  }, [userProfile.columnPreferences]);

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return [...chassisSizeTypeEntries, ...containerSizeTypeEntries, ...gensetSizeTypeEntries]
      .filter(e => bookingDetails.flatMap(({ iso }) => iso).some(sizeType => e[1].includes(sizeType ?? '')))
      .map(e => ({ label: e[0], value: e[1][0] }));
  }, [bookingDetails, chassisSizeTypeEntries, containerSizeTypeEntries, gensetSizeTypeEntries]);

  const { handleSetUnit, validateUnitNumber } = useReservation({
    bookingDetails,
    customerId: booking.customer?.id,
    locationId: booking.location?.id,
    setUnit
  });
  const setUnitNumber = useCallback((unitNumber: string) => handleSetUnit(undefined, unitNumber), [handleSetUnit]);
  const setSizeType = useCallback((sizeType: string) => handleSetUnit(sizeType), [handleSetUnit]);

  const handleReset = useCallback(() => {
    setUnit({});
    setDeleteUnit(undefined);
    setLoading(false);
    closeDialog();
    closeSubmitDialog();
    setUnitIsActive(null);
  }, [setDeleteUnit, setUnit, closeDialog]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      setLoading(true);
      if (!booking.id) return;
      await reserveUnit(booking.id, unit);
      dataTableRef.current?.forceFetch();
      handleReset();
    },
    [booking, unit, handleReset, reserveUnit]
  );

  const handleDelete: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      setLoading(true);
      if (!booking.id || !deleteUnit?.id) return;
      await unreserveUnit(booking.id, deleteUnit.id);
      dataTableRef.current?.forceFetch();
      handleReset();
    },
    [booking, deleteUnit?.id, handleReset, unreserveUnit]
  );

  const fetchData = useMemo(() => fetchActiveUnitsUngrouped({ bookingId: booking.id }), [booking.id]);

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>UNITS</h4>
        {dayjs(booking.expirationDate).isAfter(dayjs(new Date())) &&
          [
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.SENIOR_BOOKING,
            Role.BOOKING,
            Role.GATE_BOOKING,
            Role.ADMIN_CUSTOMER,
            Role.CUSTOMER,
            Role.EXTERNAL
          ].includes(userProfile.role) && (
            <button className="btn-primary" onClick={openDialog}>
              add unit
            </button>
          )}
      </div>
      <div className="card-body">
        <DataTable
          ref={dataTableRef}
          fetchData={fetchData}
          fetchCsv={downloadUngroupedCsv({ bookingId: `${booking.id}` })}
          columns={unitDataColumns}
          initialColumns={initialColumns}
          initialPage={{ pageNum: 0, pageSize: 25, sortField: 'createdDate', sortDirection: 'desc' }}
          getRowActions={(row: ActiveUnit) => [
            <button
              className="btn-back"
              disabled={!!row.deliveredDate}
              onClick={() => {
                setDeleteUnit(row);
                openDialog();
              }}
            >
              <span className="material-icons icon-large">cancel</span>
            </button>
          ]}
        />
      </div>
      <Dialog ref={ref} closeDialog={handleReset}>
        <Loader loading={loading}>
          {deleteUnit ? (
            <form className="d-flex flex-column g-3" onReset={handleReset} onSubmit={handleDelete}>
              <h3 className="text-info-main">Remove Reserved Unit</h3>
              <p className="text-info-dark">
                Are you sure you want to remove <b>{deleteUnit.unitNumber}</b> from <b>#{booking.bookingNumber}</b>?
              </p>
              <div className="grid-3 mt-3">
                <button className="btn-outline col-start-2" type="reset">
                  cancel
                </button>
                <button className="btn-danger col-start-3" type="submit">
                  confirm
                </button>
              </div>
            </form>
          ) : (
            <form className="card-primary" onReset={handleReset} onSubmit={handleSubmit}>
              <div className="card-header">
                <h4>RESERVE UNIT</h4>
                <div className="grid-2">
                  <button type="reset" className="btn-outline">
                    cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    submit
                  </button>
                </div>
              </div>
              <div className="card-body grid-2" style={{ padding: 12 }}>
                <div>
                  <TextField
                    required
                    label="UNIT NUMBER"
                    value={unit.unitNumber ?? ''}
                    onChange={setUnitNumber}
                    validate={validateUnitNumber}
                  />
                  <div
                    hidden={unitIsActive === null || !unit.unitNumber?.length}
                    className='margin-top-8px text-dark'
                  >
                      {`Unit ${unitIsActive ? 'is' : 'is not'} in yard`}
                  </div>
                </div>
                <SearchableSelect
                  containerClass="text-field"
                  inputClass={!unit.sizeType ? 'blank' : ''}
                  label="SIZE TYPE"
                  onValueSelected={setSizeType}
                  options={sizeTypeOptions}
                  value={unit.sizeType ?? ''}
                  required
                />
              </div>
            </form>
          )}
        </Loader>
      </Dialog>
    </div>
  );
};

export default BookingUnits;
